// Modo vendedor (decisión D60). "Mis visitas de hoy": los clientes de la cartera a los que les toca hoy (y los que
// visitó aunque no les tocara), en lista o en el mapa, con cómo va cada visita y lo último que pidieron; abajo, el resto
// de la cartera. La visita de un cliente: check-in con el GPS, tomar el pedido (con sus precios, o "lo de siempre") o
// anotar por qué no compró. Sin señal todo queda guardado y se manda solo cuando vuelve.
import {
  ESTADO_VISITA_ETIQUETA,
  formatoFecha,
  formatoGs,
  formatoHora,
  MOTIVO_NO_COMPRA_ETIQUETA,
  MOTIVOS_NO_COMPRA,
  type MotivoNoCompra,
  textoDias,
} from '../compartido';
import { cx, HojaInferior, Icono, useToast } from '@reparto/ui';
import { lazy, type ReactNode, Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { Aviso, Boton, ErrorCarga, TituloSeccion, Vacio } from '../componentes/base';
import { Pantalla } from '../componentes/Marco';
import type { PuntoVisita } from '../componentes/MapaVisitas';
import { mensajeDeError } from '../datos/api';
import { useConsulta } from '../datos/consultas';
import { elegirNegocio, useCuenta } from '../datos/cuenta';
import { fechaLarga, plural, primerNombre, telefonoVisible } from '../datos/formato';
import { useVendedor } from '../datos/sesion';
import type { UltimoPedido, Visita } from '../datos/tipos';
import {
  alEnviarPendiente,
  cargarVisitas,
  descartarPendiente,
  enviarPendientes,
  type EstadoLocal,
  estadoConPendientes,
  hacerCheckin,
  marcarNoCompro,
  type Pendiente,
  reintentarPendiente,
  usePendientes,
} from '../datos/vendedor';

const MapaVisitas = lazy(() => import('../componentes/MapaVisitas'));

const CLAVE = 'vendedor:visitas';

const CHIP: Record<EstadoLocal['estado'], { clase: string; icono: string }> = {
  por_visitar: { clase: 'bg-stone-100 text-stone-700', icono: 'schedule' },
  en_el_local: { clase: 'bg-sky-100 text-sky-900', icono: 'storefront' },
  con_pedido: { clase: 'bg-emerald-100 text-emerald-900', icono: 'check_circle' },
  no_compro: { clase: 'bg-amber-100 text-amber-900', icono: 'remove_shopping_cart' },
};

export function ChipVisita({ estado, sinMandar }: EstadoLocal) {
  const t = CHIP[estado];
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-semibold whitespace-nowrap',
        t.clase,
      )}
      data-estado-visita={estado}
    >
      <Icono nombre={sinMandar ? 'cloud_off' : t.icono} tamano={17} relleno={!sinMandar} />
      {ESTADO_VISITA_ETIQUETA[estado]}
      {sinMandar ? ' · sin mandar' : ''}
    </span>
  );
}

/** Lo que mandó sin señal llegó: lo dice una vez (en cualquier pantalla de las visitas). */
function useAvisosEnvio(alEnviar: () => void) {
  const { mostrar } = useToast();
  useEffect(
    () =>
      alEnviarPendiente((p, pedido) => {
        alEnviar();
        if (p.tipo === 'pedido')
          mostrar(`Llegó el pedido de ${p.cliente}${pedido ? ` (${pedido.numero_visible})` : ''}.`, {
            tipo: 'ok',
            icono: 'cloud_done',
          });
      }),
    [],
  );
}

// ---------- Mis visitas de hoy ----------

export function Visitas() {
  const vendedor = useVendedor()!;
  const perfil = useCuenta((s) => s.perfil)!;
  const navegar = useNavigate();
  const c = useConsulta(CLAVE, cargarVisitas);
  const pendientes = usePendientes((s) => s.lista);
  const [vista, setVista] = useState<'lista' | 'mapa'>('lista');
  const [verOtros, setVerOtros] = useState(false);
  useAvisosEnvio(() => void c.recargar());
  const d = c.datos;

  const filas = useMemo(
    () =>
      (d?.datos ?? []).map((x, i) => ({
        ...x,
        numero: i + 1,
        local: estadoConPendientes(x.cliente.id, x.visita, pendientes),
      })),
    [d, pendientes],
  );
  const cuenta = (e: EstadoLocal['estado']) => filas.filter((f) => f.local.estado === e).length;
  const puntos: PuntoVisita[] = filas
    .filter((f) => f.cliente.ubicacion)
    .map((f) => ({
      id: f.cliente.id,
      numero: f.numero,
      nombre: f.cliente.nombre,
      lat: f.cliente.ubicacion!.lat,
      lng: f.cliente.ubicacion!.lng,
      estado: f.local.estado,
    }));

  return (
    <Pantalla
      titulo="Mis visitas de hoy"
      subtitulo={`${primerNombre(vendedor.nombre) ?? vendedor.nombre} · ${d ? fechaLarga(d.fecha) : perfil.empresa.nombre}`}
      amplia
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        <Pendientes />
        {c.cargando ? (
          <EsqueletoVisitas />
        ) : !d ? (
          <ErrorCarga mensaje={c.error} sinRed={c.sinRed} onReintentar={() => void c.recargar()} />
        ) : (
          <>
            {d.guardada ? (
              <Aviso tono="info" icono="signal_disconnected" titulo="Sin señal">
                Es la lista que se guardó la última vez ({formatoFecha(d.fecha)}). Lo que anotes se manda cuando vuelva
                la señal.
              </Aviso>
            ) : c.error ? (
              <ErrorCarga compacto mensaje={c.error} sinRed={c.sinRed} onReintentar={() => void c.recargar()} />
            ) : null}

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Resumen del día">
              <Numero n={cuenta('por_visitar')} texto="Por visitar" icono="schedule" />
              <Numero n={cuenta('en_el_local')} texto="En el local" icono="storefront" />
              <Numero n={cuenta('con_pedido')} texto="Con pedido" icono="check_circle" tono="ok" />
              <Numero n={cuenta('no_compro')} texto="No compró" icono="remove_shopping_cart" tono="aviso" />
            </div>

            {filas.length ? (
              <>
                <div role="tablist" aria-label="Ver como" className="flex gap-1 rounded-full bg-stone-100 p-1">
                  {(['lista', 'mapa'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      role="tab"
                      aria-selected={vista === v}
                      onClick={() => setVista(v)}
                      className={cx(
                        'inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-full text-[15px] font-semibold',
                        vista === v ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-600 hover:text-stone-900',
                      )}
                    >
                      <Icono nombre={v === 'lista' ? 'list' : 'map'} tamano={20} />
                      {v === 'lista' ? 'Lista' : 'Mapa'}
                    </button>
                  ))}
                </div>

                {vista === 'mapa' ? (
                  <div className="overflow-hidden rounded-3xl border border-stone-200 shadow-sm">
                    <div className="h-[60vh] min-h-80">
                      <Suspense
                        fallback={
                          <div className="grid h-full place-items-center text-stone-600">Cargando el mapa…</div>
                        }
                      >
                        <MapaVisitas puntos={puntos} onElegir={(id) => navegar(`/visitas/${id}`)} />
                      </Suspense>
                    </div>
                    {puntos.length < filas.length ? (
                      <p className="border-t border-stone-200 bg-white px-4 py-2 text-[15px] text-stone-600">
                        {plural(filas.length - puntos.length, 'cliente no tiene', 'clientes no tienen')} la ubicación
                        marcada: están en la lista.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <ol className="flex flex-col gap-3" aria-label="Clientes de hoy">
                    {filas.map((f) => (
                      <li key={f.cliente.id}>
                        <Link
                          to={`/visitas/${f.cliente.id}`}
                          className="flex gap-3 rounded-3xl border border-stone-200 bg-white p-4 shadow-sm transition-colors hover:border-primary-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                          data-visita={f.cliente.id}
                        >
                          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary-50 text-base font-bold text-primary-800">
                            {f.numero}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                              <span className="min-w-0 text-lg leading-snug font-bold text-stone-900">
                                {f.cliente.nombre}
                              </span>
                              <ChipVisita {...f.local} />
                            </span>
                            <span className="mt-0.5 block text-[15px] text-stone-600">
                              {[f.cliente.direccion, f.cliente.zona].filter(Boolean).join(' · ') || 'Sin dirección'}
                            </span>
                            {!f.programada ? (
                              <span className="mt-1 block text-sm font-medium text-sky-800">Fuera de programa</span>
                            ) : null}
                            {f.visita?.lejos ? (
                              <span className="mt-1 flex items-center gap-1 text-sm font-medium text-amber-800">
                                <Icono nombre="wrong_location" tamano={17} />
                                Check-in a {f.visita.distancia_m} m del local
                              </span>
                            ) : null}
                            <UltimoTexto u={f.ultimo_pedido} />
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                )}
              </>
            ) : (
              <Vacio
                icono="event_available"
                titulo="Hoy no tenés visitas programadas"
                texto={
                  d.otros.length
                    ? 'Abajo están los clientes de tu cartera: podés visitar a cualquiera.'
                    : 'Todavía no tenés clientes en tu cartera. Pedile a la distribuidora que te los asigne.'
                }
              />
            )}

            {d.otros.length ? (
              <section aria-labelledby="otros">
                <button
                  type="button"
                  aria-expanded={verOtros}
                  onClick={() => setVerOtros((v) => !v)}
                  className="flex min-h-12 w-full items-center justify-between gap-2 rounded-2xl px-1 text-left"
                >
                  <span id="otros" className="text-lg font-bold text-stone-900">
                    Otros clientes de tu cartera ({d.otros.length})
                  </span>
                  <Icono nombre={verOtros ? 'expand_less' : 'expand_more'} />
                </button>
                {verOtros ? (
                  <ul className="mt-1 divide-y divide-stone-100 rounded-3xl border border-stone-200 bg-white shadow-sm">
                    {d.otros.map((o) => (
                      <li key={o.id}>
                        <Link
                          to={`/visitas/${o.id}`}
                          className="flex min-h-14 items-center gap-3 px-4 py-2.5 hover:bg-stone-50"
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold text-stone-900">{o.nombre}</span>
                            <span className="block truncate text-sm text-stone-600">
                              {[o.direccion, textoDias(o.dias_visita)].filter(Boolean).join(' · ')}
                            </span>
                          </span>
                          <Icono nombre="chevron_right" className="shrink-0 text-stone-500" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </div>
    </Pantalla>
  );
}

function Numero({ n, texto, icono, tono }: { n: number; texto: string; icono: string; tono?: 'ok' | 'aviso' }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white px-3 py-2.5 shadow-sm">
      <p className="flex items-center gap-1 text-sm text-stone-600">
        <Icono
          nombre={icono}
          tamano={17}
          className={tono === 'ok' ? 'text-emerald-700' : tono === 'aviso' ? 'text-amber-800' : 'text-stone-500'}
        />
        {texto}
      </p>
      <p className="text-2xl font-bold text-stone-900 tabular-nums">{n}</p>
    </div>
  );
}

function UltimoTexto({ u }: { u: UltimoPedido | null }) {
  if (!u) return <span className="mt-1.5 block text-sm text-stone-500">Todavía no tiene pedidos entregados</span>;
  return (
    <span className="mt-1.5 flex items-start gap-1 text-sm text-stone-700">
      <Icono nombre="history" tamano={17} className="mt-px shrink-0 text-stone-500" />
      <span>
        Último: {formatoFecha(u.fecha)} · {plural(u.bultos, 'bulto', 'bultos')} · {formatoGs(u.monto)}
      </span>
    </span>
  );
}

function EsqueletoVisitas() {
  return (
    <div role="status" aria-label="Cargando tus visitas" className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[74px] animate-pulse rounded-2xl bg-stone-200/70" />
        ))}
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-28 animate-pulse rounded-3xl bg-stone-200/70" />
      ))}
    </div>
  );
}

/** Lo que quedó guardado sin señal: se manda solo; lo que la API no aceptó, se mira acá. */
export function Pendientes() {
  const lista = usePendientes((s) => s.lista);
  const enviando = usePendientes((s) => s.enviando);
  if (!lista.length) return null;
  const conError = lista.filter((p) => p.error);
  const esperando = lista.length - conError.length;
  return (
    <div className="flex flex-col gap-2" data-pendientes={lista.length}>
      {esperando ? (
        <Aviso
          tono="aviso"
          icono="cloud_upload"
          titulo={`${plural(esperando, 'cosa', 'cosas')} sin mandar`}
          accion={
            <Boton
              variante="secundario"
              tamano="sm"
              className="h-11!"
              icono="sync"
              cargando={enviando}
              onClick={() => void enviarPendientes()}
            >
              Mandar ahora
            </Boton>
          }
        >
          {lista
            .filter((p) => !p.error)
            .map(textoPendiente)
            .join(' · ')}
          . Se mandan solas cuando vuelve la señal.
        </Aviso>
      ) : null}
      {conError.map((p) => (
        <Aviso
          key={p.id}
          tono="peligro"
          icono="error"
          titulo={`No se pudo mandar: ${textoPendiente(p)}`}
          accion={
            <>
              <Boton
                variante="secundario"
                tamano="sm"
                className="h-11!"
                icono="refresh"
                onClick={() => void reintentarPendiente(p.id)}
              >
                Probar de nuevo
              </Boton>
              <Boton
                variante="fantasma"
                tamano="sm"
                className="h-11!"
                icono="delete"
                onClick={() => descartarPendiente(p.id)}
              >
                Descartar
              </Boton>
            </>
          }
        >
          {p.error}
        </Aviso>
      ))}
    </div>
  );
}

function textoPendiente(p: Pendiente): string {
  const hora = formatoHora(p.hora);
  if (p.tipo === 'pedido')
    return `pedido de ${p.cliente} (${plural(p.bultos, 'bulto', 'bultos')}${p.monto ? `, ${formatoGs(p.monto)}` : ''}, ${hora})`;
  if (p.tipo === 'no_compro')
    return `${p.cliente}: no compró (${MOTIVO_NO_COMPRA_ETIQUETA[p.motivo].toLowerCase()}, ${hora})`;
  return `check-in en ${p.cliente} (${hora})`;
}

// ---------- La visita a un cliente ----------

/** Lo que vuelve a la visita después de guardar un pedido sin señal (desde el carrito). */
export interface EstadoVuelta {
  pedidoGuardado?: boolean;
}

export function VisitaCliente() {
  const { clienteId = '' } = useParams();
  const navegar = useNavigate();
  const { state } = useLocation() as { state: EstadoVuelta | null };
  const perfil = useCuenta((s) => s.perfil)!;
  const c = useConsulta(CLAVE, cargarVisitas);
  const pendientes = usePendientes((s) => s.lista);
  const { mostrar } = useToast();
  const [checkin, setCheckin] = useState(false);
  const [noCompro, setNoCompro] = useState(false);
  const [nota, setNota] = useState<{ tono: 'ok' | 'aviso' | 'info'; texto: string } | null>(
    state?.pedidoGuardado
      ? { tono: 'aviso', texto: 'Sin señal: el pedido quedó guardado en el celular y se manda solo cuando vuelva.' }
      : null,
  );
  useAvisosEnvio(() => void c.recargar());

  const negocio = perfil.clientes.find((x) => x.id === clienteId) ?? null;
  const dato = c.datos?.datos.find((x) => x.cliente.id === clienteId) ?? null;
  const otro = c.datos?.otros.find((x) => x.id === clienteId) ?? null;
  const [visita, setVisita] = useState<Visita | null>(dato?.visita ?? null);
  useEffect(() => setVisita(dato?.visita ?? null), [dato?.visita]);

  if (!negocio)
    return (
      <Pantalla titulo="Visita" volver="/visitas">
        <Vacio
          icono="person_off"
          titulo="Ese cliente no está en tu cartera"
          texto="Puede que la distribuidora lo haya pasado a otro preventista. Volvé a tus visitas."
        >
          <Boton tamano="lg" icono="route" onClick={() => navegar('/visitas', { replace: true })}>
            Mis visitas
          </Boton>
        </Vacio>
      </Pantalla>
    );

  const cliente = {
    nombre: negocio.nombre,
    direccion: dato?.cliente.direccion ?? negocio.direccion,
    referencia: dato?.cliente.referencia ?? negocio.referencia ?? null,
    telefono: dato?.cliente.telefono ?? negocio.telefono ?? null,
    contacto: dato?.cliente.contacto ?? negocio.contacto,
    ubicacion: dato?.cliente.ubicacion ?? negocio.ubicacion ?? null,
    dias: dato?.cliente.dias_visita ?? otro?.dias_visita ?? negocio.dias_visita ?? [],
  };
  const local = estadoConPendientes(clienteId, visita, pendientes);
  const conCheckin = !!visita?.checkin_en || pendientes.some((p) => p.cliente_id === clienteId && p.tipo === 'checkin');
  const ultimo = dato?.ultimo_pedido ?? null;
  const umbral = c.datos?.distancia_checkin_m ?? null;

  async function registrarCheckin() {
    setCheckin(true);
    setNota(null);
    try {
      const r = await hacerCheckin({ id: clienteId, nombre: negocio!.nombre });
      if (r.visita) {
        setVisita(r.visita);
        void c.recargar();
      }
      setNota(
        r.guardado
          ? { tono: 'aviso', texto: 'Sin señal: el check-in quedó guardado con la hora de ahora y se manda solo.' }
          : r.sinGps
            ? { tono: 'info', texto: 'Check-in hecho, sin el GPS: queda la hora, sin la distancia al local.' }
            : r.visita?.lejos
              ? {
                  tono: 'aviso',
                  texto: `Check-in hecho, pero estás a ${r.visita.distancia_m} m del local: queda marcado.`,
                }
              : { tono: 'ok', texto: 'Check-in hecho.' },
      );
    } catch (e) {
      mostrar(mensajeDeError(e), { tipo: 'error' });
    } finally {
      setCheckin(false);
    }
  }

  function pedir(ruta: string) {
    elegirNegocio(clienteId);
    navegar(ruta);
  }

  return (
    <Pantalla
      titulo={negocio.nombre}
      subtitulo={dato?.programada === false || (!dato && otro) ? 'Fuera de programa' : textoDias(cliente.dias)}
      volver="/visitas"
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <ChipVisita {...local} />
          {visita?.checkin_en ? (
            <span className="text-[15px] text-stone-600">
              Check-in {formatoHora(visita.checkin_en)}
              {visita.distancia_m !== null ? ` · a ${visita.distancia_m} m del local` : ' · sin GPS'}
            </span>
          ) : null}
        </div>

        {nota ? (
          <Aviso tono={nota.tono} icono={nota.tono === 'ok' ? 'where_to_vote' : undefined}>
            <span role="status">{nota.texto}</span>
          </Aviso>
        ) : null}
        <Pendientes />

        <section className="rounded-3xl border border-stone-200 bg-white p-4 shadow-sm" aria-label="El local">
          <dl className="divide-y divide-stone-100 text-[17px]">
            <Fila titulo="Dirección">{cliente.direccion ?? 'Sin dirección'}</Fila>
            {cliente.referencia ? <Fila titulo="Referencia">{cliente.referencia}</Fila> : null}
            {cliente.contacto ? <Fila titulo="Contacto">{cliente.contacto}</Fila> : null}
            {cliente.telefono ? (
              <Fila titulo="Teléfono">
                <a
                  href={`tel:${cliente.telefono}`}
                  className="font-semibold text-primary-700 underline-offset-4 hover:underline"
                >
                  {telefonoVisible(cliente.telefono)}
                </a>
              </Fila>
            ) : null}
          </dl>
          {cliente.ubicacion ? (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${cliente.ubicacion.lat},${cliente.ubicacion.lng}`}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex min-h-11 items-center gap-1.5 rounded-full px-2 text-[15px] font-semibold text-primary-700 hover:bg-primary-50"
            >
              <Icono nombre="directions" tamano={22} />
              Cómo llegar
            </a>
          ) : (
            <p className="mt-2 text-sm text-stone-600">
              El local no tiene la ubicación marcada: el check-in queda sin la distancia.
            </p>
          )}
        </section>

        {local.estado === 'con_pedido' && visita?.pedido ? (
          <Aviso
            tono="ok"
            titulo={`Pedido ${visita.pedido.numero_visible} · ${formatoGs(visita.pedido.monto)}`}
            accion={
              <Boton
                variante="secundario"
                tamano="sm"
                className="h-11!"
                icono="receipt_long"
                onClick={() => pedir(`/pedidos/${visita.pedido!.id}`)}
              >
                Ver el pedido
              </Boton>
            }
          >
            {perfil.preventa?.aprobacion === 'automatica'
              ? 'Se aprobó solo: pasa directo a preparación.'
              : 'Lo revisa Pedidos de la distribuidora antes de prepararlo.'}
          </Aviso>
        ) : null}
        {local.estado === 'no_compro' && visita?.motivo ? (
          <Aviso
            tono="aviso"
            icono="remove_shopping_cart"
            titulo={`No compró: ${MOTIVO_NO_COMPRA_ETIQUETA[visita.motivo].toLowerCase()}`}
          >
            {visita.motivo_texto ?? 'Si al final pide, tomale el pedido igual: la visita queda con pedido.'}
          </Aviso>
        ) : null}

        <div className="flex flex-col gap-3">
          {!conCheckin ? (
            <Boton bloque tamano="lg" icono="where_to_vote" cargando={checkin} onClick={() => void registrarCheckin()}>
              {checkin ? 'Buscando el GPS…' : 'Estoy en el local (check-in)'}
            </Boton>
          ) : null}
          <Boton
            bloque
            tamano="lg"
            variante={conCheckin ? 'primario' : 'secundario'}
            icono="add_shopping_cart"
            onClick={() => pedir('/')}
          >
            {local.estado === 'con_pedido' ? 'Tomar otro pedido' : 'Tomar el pedido'}
          </Boton>
          {ultimo ? (
            <Boton bloque tamano="lg" variante="secundario" icono="replay" onClick={() => pedir('/lo-de-siempre')}>
              Lo de siempre
            </Boton>
          ) : null}
          {local.estado !== 'con_pedido' ? (
            <Boton
              bloque
              tamano="lg"
              variante="secundario"
              icono="remove_shopping_cart"
              onClick={() => setNoCompro(true)}
            >
              No compró
            </Boton>
          ) : null}
        </div>

        {ultimo ? (
          <section aria-labelledby="ultimo" className="rounded-3xl border border-stone-200 bg-white p-4 shadow-sm">
            <TituloSeccion id="ultimo">Lo último que pidió</TituloSeccion>
            <p className="text-[15px] text-stone-700">
              {ultimo.numero_visible} · para el {formatoFecha(ultimo.fecha)} ·{' '}
              {plural(ultimo.bultos, 'bulto', 'bultos')} · <b className="tabular-nums">{formatoGs(ultimo.monto)}</b>
            </p>
            <ul className="mt-2 list-disc pl-5 text-[15px] text-stone-800">
              {ultimo.articulos.slice(0, 8).map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
            {ultimo.articulos.length > 8 ? (
              <p className="mt-1 text-sm text-stone-600">
                y {plural(ultimo.articulos.length - 8, 'artículo más', 'artículos más')}
              </p>
            ) : null}
          </section>
        ) : null}

        {umbral !== null ? (
          <p className="text-center text-sm text-stone-500">
            El check-in a más de {umbral} m del local queda marcado como lejos.
          </p>
        ) : null}
      </div>

      <HojaNoCompro
        abierta={noCompro}
        onCerrar={() => setNoCompro(false)}
        cliente={{ id: clienteId, nombre: negocio.nombre }}
        alGuardar={(v, guardado) => {
          if (v) setVisita(v);
          setNota(
            guardado
              ? { tono: 'aviso', texto: 'Sin señal: quedó guardado y se manda solo cuando vuelva.' }
              : { tono: 'ok', texto: 'Listo: quedó anotado por qué no compró.' },
          );
          if (!guardado) void c.recargar();
        }}
      />
    </Pantalla>
  );
}

function Fila({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-2.5">
      <dt className="shrink-0 text-stone-600">{titulo}</dt>
      <dd className="min-w-0 text-right font-medium break-words text-stone-900">{children}</dd>
    </div>
  );
}

function HojaNoCompro({
  abierta,
  onCerrar,
  cliente,
  alGuardar,
}: {
  abierta: boolean;
  onCerrar: () => void;
  cliente: { id: string; nombre: string };
  alGuardar: (v: Visita | null, guardado: boolean) => void;
}) {
  const [motivo, setMotivo] = useState<MotivoNoCompra | null>(null);
  const [texto, setTexto] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!abierta) return;
    setMotivo(null);
    setTexto('');
    setError(null);
  }, [abierta]);

  async function guardar() {
    if (!motivo) return setError('Elegí el motivo.');
    if (motivo === 'otro' && !texto.trim()) return setError('Contá en pocas palabras qué pasó.');
    setOcupado(true);
    setError(null);
    try {
      const r = await marcarNoCompro(cliente, motivo, texto.trim() || null);
      alGuardar(r.visita, r.guardado);
      onCerrar();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <HojaInferior abierta={abierta} onCerrar={onCerrar} titulo={`¿Por qué no compró ${cliente.nombre}?`}>
      <fieldset className="flex flex-col gap-2">
        <legend className="sr-only">Motivo</legend>
        {MOTIVOS_NO_COMPRA.map((m) => (
          <label
            key={m}
            className={cx(
              'flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl border-2 px-4 text-[17px] font-medium',
              motivo === m ? 'border-primary-600 bg-primary-50' : 'border-stone-200 bg-white',
            )}
          >
            <input
              type="radio"
              name="motivo"
              value={m}
              checked={motivo === m}
              onChange={() => setMotivo(m)}
              className="h-5 w-5 accent-primary-600"
            />
            {MOTIVO_NO_COMPRA_ETIQUETA[m]}
          </label>
        ))}
      </fieldset>
      {motivo === 'otro' ? (
        <label className="mt-3 block">
          <span className="text-base font-semibold text-stone-800">¿Qué pasó?</span>
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            maxLength={200}
            rows={2}
            className="mt-2 w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 text-[17px] text-stone-900 focus:border-primary-600 focus:outline-2 focus:outline-primary-600"
          />
        </label>
      ) : null}
      {error ? (
        <p role="alert" className="mt-3 text-[15px] font-medium text-rose-800">
          {error}
        </p>
      ) : null}
      <div className="mt-5 flex flex-col gap-3">
        <Boton bloque tamano="lg" icono="check" cargando={ocupado} onClick={() => void guardar()}>
          Guardar
        </Boton>
        <Boton bloque tamano="lg" variante="secundario" onClick={onCerrar}>
          Cancelar
        </Boton>
      </div>
    </HojaInferior>
  );
}
